import React, { useState, useEffect } from 'react';
import { FaFileInvoice, FaArrowLeft, FaEye, FaPaperPlane } from 'react-icons/fa';
import api from '../services/api';

const Invoices = () => {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [asSeller, setAsSeller] = useState(true);

    // Szczegóły faktury
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [invoiceItems, setInvoiceItems] = useState([]);
    const [loadingDetails, setLoadingDetails] = useState(false);

    // Wysyłka do KSeF
    const [sending, setSending] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    // Mapowanie użytkowników
    const [users, setUsers] = useState([]);
    const userIdToName = {};
    const userIdToUser = {};
    users.forEach(u => {
        userIdToName[u.id] = u.full_name || u.username;
        userIdToUser[u.id] = u;
    });

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await api.get('/users/');
                setUsers(response.data);
            } catch (error) {
                console.error('Failed to fetch users', error);
            }
        };
        fetchUsers();
    }, []);

    useEffect(() => {
        const fetchInvoices = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await api.get(`/invoices/?as_seller=${asSeller}`);
                setInvoices(response.data);
            } catch (error) {
                console.error('Failed to fetch invoices', error);
                setError('Nie udało się pobrać listy faktur');
            } finally {
                setLoading(false);
            }
        };
        fetchInvoices();
    }, [asSeller]);

    const viewInvoiceDetails = async (invoice) => {
        try {
            setLoadingDetails(true);
            setSelectedInvoice(invoice);

            // Pobierz pozycje faktury
            const itemsResponse = await api.get(`/invoices/${invoice.id}/items`);
            setInvoiceItems(itemsResponse.data);
        } catch (error) {
            console.error('Failed to fetch invoice details', error);
            setError('Nie udało się pobrać szczegółów faktury');
        } finally {
            setLoadingDetails(false);
        }
    };

    const closeDetails = () => {
        setSelectedInvoice(null);
        setInvoiceItems([]);
    };

    const handleSendToKsef = async (invoice) => {
        if (!window.confirm(`Czy na pewno chcesz wysłać fakturę ${invoice.invoice_number} do KSeF?`)) return;

        try {
            setSending(invoice.id);
            setError(null);
            setSuccessMessage('');
            const result = await api.post(`/invoices/${invoice.id}/send`);

            // Aktualizuj fakturę na liście
            const updatedStatus = result.data.status || 'pending';
            const updatedKsef = result.data.ksef_number || null;
            setInvoices(invoices.map(inv =>
                inv.id === invoice.id
                    ? { ...inv, ksef_status: updatedStatus, ksef_number: updatedKsef }
                    : inv
            ));

            // Aktualizuj szczegóły jeśli otwarte
            if (selectedInvoice && selectedInvoice.id === invoice.id) {
                setSelectedInvoice({ ...selectedInvoice, ksef_status: updatedStatus, ksef_number: updatedKsef });
            }

            setSuccessMessage(`Faktura ${invoice.invoice_number} wysłana do KSeF`);
        } catch (error) {
            console.error('Failed to send invoice to KSeF', error);
            const detail = error.response?.data?.detail;
            if (typeof detail === 'string') {
                setError(translateError(detail));
            } else {
                setError('Nie udało się wysłać faktury do KSeF');
            }
        } finally {
            setSending(null);
        }
    };

    const canSendToKsef = (status) => ['draft', 'error'].includes(status);

    const translateError = (msg) => {
        if (!msg) return msg;
        if (/KSeF integration is disabled/i.test(msg)) return 'Integracja z KSeF jest wyłączona';
        if (/Invoice not found/i.test(msg)) return 'Nie znaleziono faktury';
        if (/Cannot send invoice with status/i.test(msg)) return msg.replace(/Cannot send invoice with status: (\S+)/i, 'Nie można wysłać faktury o statusie: $1');
        if (/XML generation failed/i.test(msg)) return msg.replace(/XML generation failed: (.+)/i, 'Błąd generowania XML: $1');
        if (/Not enough permissions/i.test(msg)) return 'Brak uprawnień';
        return msg;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('pl-PL');
    };

    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined) return '-';
        return new Intl.NumberFormat('pl-PL', {
            style: 'currency',
            currency: 'PLN'
        }).format(amount);
    };

    const formatNumber = (amount) => {
        if (amount === null || amount === undefined) return '-';
        return new Intl.NumberFormat('pl-PL', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    const amountInWords = (amount) => {
        const ones = ['', 'jeden', 'dwa', 'trzy', 'cztery', 'pięć', 'sześć', 'siedem', 'osiem', 'dziewięć'];
        const teens = ['dziesięć', 'jedenaście', 'dwanaście', 'trzynaście', 'czternaście', 'piętnaście', 'szesnaście', 'siedemnaście', 'osiemnaście', 'dziewiętnaście'];
        const tens = ['', 'dziesięć', 'dwadzieścia', 'trzydzieści', 'czterdzieści', 'pięćdziesiąt', 'sześćdziesiąt', 'siedemdziesiąt', 'osiemdziesiąt', 'dziewięćdziesiąt'];
        const hundreds = ['', 'sto', 'dwieście', 'trzysta', 'czterysta', 'pięćset', 'sześćset', 'siedemset', 'osiemset', 'dziewięćset'];

        const pluralForm = (n, one, few, many) => {
            if (n === 1) return one;
            const lastDigit = n % 10;
            const lastTwo = n % 100;
            if (lastDigit >= 2 && lastDigit <= 4 && (lastTwo < 12 || lastTwo > 14)) return few;
            return many;
        };

        const convertChunk = (n) => {
            if (n === 0) return '';
            let result = '';
            if (n >= 100) {
                result += hundreds[Math.floor(n / 100)] + ' ';
                n %= 100;
            }
            if (n >= 10 && n < 20) {
                result += teens[n - 10];
                return result.trim();
            }
            if (n >= 20) {
                result += tens[Math.floor(n / 10)] + ' ';
                n %= 10;
            }
            if (n > 0) {
                result += ones[n];
            }
            return result.trim();
        };

        const abs = Math.abs(amount);
        const zlote = Math.floor(abs);
        const grosze = Math.round((abs - zlote) * 100);
        let parts = [];

        if (zlote === 0) {
            parts.push('zero');
        } else {
            if (zlote >= 1000000) {
                const m = Math.floor(zlote / 1000000);
                if (m === 1) parts.push('milion');
                else parts.push(convertChunk(m) + ' ' + pluralForm(m, 'milion', 'miliony', 'milionów'));
            }
            const afterMillions = zlote % 1000000;
            if (afterMillions >= 1000) {
                const t = Math.floor(afterMillions / 1000);
                if (t === 1) parts.push('tysiąc');
                else parts.push(convertChunk(t) + ' ' + pluralForm(t, 'tysiąc', 'tysiące', 'tysięcy'));
            }
            const lastThree = zlote % 1000;
            if (lastThree > 0) {
                parts.push(convertChunk(lastThree));
            }
        }

        let result = parts.join(' ') + ' PLN';
        if (grosze > 0) {
            result += ' ' + convertChunk(grosze) + ' gr';
        } else {
            result += ' zero gr';
        }
        return result;
    };

    const getBankName = (accountNumber) => {
        if (!accountNumber) return null;
        const digits = accountNumber.replace(/\D/g, '');
        let sortCode;
        if (digits.length === 28) sortCode = digits.substring(4, 8);
        else if (digits.length === 26) sortCode = digits.substring(2, 6);
        else return null;
        const banks = {
            '1010': 'Narodowy Bank Polski',
            '1020': 'PKO Bank Polski S.A.',
            '1030': 'Citi Handlowy',
            '1050': 'ING Bank Śląski S.A.',
            '1090': 'Santander Bank Polska S.A.',
            '1130': 'Bank Gospodarstwa Krajowego',
            '1140': 'mBank S.A.',
            '1160': 'Bank Millennium S.A.',
            '1240': 'Bank Pekao S.A.',
            '1320': 'Bank Pocztowy S.A.',
            '1540': 'BOŚ Bank S.A.',
            '1610': 'SGB-Bank S.A.',
            '1680': 'Plus Bank S.A.',
            '1750': 'Raiffeisen Polbank',
            '1870': 'Nest Bank S.A.',
            '1930': 'Bank Polskiej Spółdzielczości S.A.',
            '1940': 'Credit Agricole Bank Polska S.A.',
            '2030': 'BNP Paribas Bank Polska S.A.',
            '2120': 'Santander Consumer Bank S.A.',
            '2160': 'Toyota Bank Polska S.A.',
            '2490': 'Alior Bank S.A.',
            '2720': 'Inbank S.A.',
            '2770': 'Volkswagen Bank GmbH',
            '2800': 'HSBC Continental Europe',
            '2850': 'BFF Bank S.A.',
            '2910': 'Aion Bank S.A.',
            '2930': 'VeloBank S.A.',
        };
        return banks[sortCode] || null;
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            'draft': { label: 'Szkic', color: 'bg-gray-200 text-gray-700' },
            'pending': { label: 'Oczekująca', color: 'bg-yellow-200 text-yellow-800' },
            'sent': { label: 'Wysłana', color: 'bg-blue-200 text-blue-800' },
            'accepted': { label: 'Zaakceptowana', color: 'bg-green-200 text-green-800' },
            'rejected': { label: 'Odrzucona', color: 'bg-red-200 text-red-800' },
            'error': { label: 'Błąd', color: 'bg-red-200 text-red-800' }
        };
        const statusInfo = statusMap[status] || { label: status, color: 'bg-gray-200 text-gray-700' };
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusInfo.color}`}>
                {statusInfo.label}
            </span>
        );
    };

    // Widok szczegółów faktury
    if (selectedInvoice) {
        return (
            <div className="min-h-screen p-4">
                <main className="container mx-auto max-w-4xl">
                    {/* Powrót + akcje */}
                    <div className="flex justify-between items-center mb-4 bg-white rounded-lg shadow-sm px-4 py-3">
                        <button
                            onClick={closeDetails}
                            className="flex items-center text-softTeal hover:text-warmOrange transition"
                        >
                            <FaArrowLeft className="mr-2" /> Powrót do listy
                        </button>
                        <div className="flex items-center gap-3">
                            {getStatusBadge(selectedInvoice.ksef_status)}
                            {canSendToKsef(selectedInvoice.ksef_status) && (
                                <button
                                    onClick={() => handleSendToKsef(selectedInvoice)}
                                    disabled={sending === selectedInvoice.id}
                                    className="bg-softTeal text-white px-4 py-2 rounded-lg hover:bg-warmOrange transition disabled:opacity-50 flex items-center text-sm"
                                >
                                    <FaPaperPlane className="mr-2" />
                                    {sending === selectedInvoice.id ? 'Wysyłanie...' : 'Wyślij do KSeF'}
                                </button>
                            )}
                        </div>
                    </div>

                    {successMessage && (
                        <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                            {successMessage}
                        </div>
                    )}
                    {error && (
                        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                            {error}
                        </div>
                    )}

                    {/* Dokument faktury */}
                    <div className="bg-white shadow-md" style={{ padding: '36px 40px', lineHeight: '1.45', fontSize: '12px' }}>
                        {loadingDetails ? (
                            <p className="text-center py-8">Ładowanie szczegółów...</p>
                        ) : (
                            <>
                                {/* Nagłówek faktury */}
                                <div className="mb-4">
                                    <div className="text-xs text-gray-600">Numer Faktury:</div>
                                    <div className="text-base font-bold">{selectedInvoice.invoice_number}</div>
                                    <div className="text-xs text-gray-600">Faktura podstawowa</div>
                                </div>

                                <hr className="border-gray-300 my-3" />

                                {/* Sprzedawca / Nabywca */}
                                <div className="flex flex-col md:flex-row gap-8 mb-4">
                                    {[
                                        { label: 'Sprzedawca', id: selectedInvoice.seller_user_id },
                                        { label: 'Nabywca', id: selectedInvoice.buyer_user_id },
                                        ...(selectedInvoice.dependent_entity_id
                                            ? [{ label: 'Podmiot trzeci', id: selectedInvoice.dependent_entity_id }]
                                            : [])
                                    ].map(({ label, id }) => {
                                        const u = userIdToUser[id];
                                        return (
                                            <div className="flex-1" key={label}>
                                                <div className="text-sm font-bold mb-2">{label}</div>
                                                {u ? (
                                                    <>
                                                        <div>{u.full_name || u.username}</div>
                                                        {u.address && <div className="mt-1">{u.address}</div>}
                                                        {u.tax_id_number && <div className="mt-1">NIP {u.tax_id_number}</div>}
                                                    </>
                                                ) : (
                                                    <div>-</div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>

                                <hr className="border-gray-300 my-3" />

                                {/* Szczegóły / Daty */}
                                <div className="text-sm font-bold mb-2">Szczegóły</div>
                                <div className="flex flex-col md:flex-row gap-8 mb-4" style={{ fontSize: '12px' }}>
                                    <div className="flex-1">
                                        <span className="text-gray-600">Data wystawienia:</span> <span>{formatDate(selectedInvoice.invoice_date)}</span>
                                    </div>
                                    <div className="flex-1">
                                        <span className="text-gray-600">Data sprzedaży:</span> <span>{formatDate(selectedInvoice.sale_date)}</span>
                                    </div>
                                </div>

                                <hr className="border-gray-300 my-3" />

                                <div className="overflow-x-auto mb-2">
                                    <table className="w-full border-collapse text-xs">
                                        <thead>
                                            <tr>
                                                <th className="bg-gray-200 border border-gray-400 px-2 py-1 text-left font-bold">Lp.</th>
                                                <th className="bg-gray-200 border border-gray-400 px-2 py-1 text-left font-bold">Nazwa towaru / usługi</th>
                                                <th className="bg-gray-200 border border-gray-400 px-2 py-1 text-right font-bold">Ilość (j.m.)</th>
                                                <th className="bg-gray-200 border border-gray-400 px-2 py-1 text-right font-bold">Cena netto</th>
                                                <th className="bg-gray-200 border border-gray-400 px-2 py-1 text-right font-bold">Wartość netto</th>
                                                <th className="bg-gray-200 border border-gray-400 px-2 py-1 text-center font-bold">VAT %</th>
                                                <th className="bg-gray-200 border border-gray-400 px-2 py-1 text-right font-bold">Wartość VAT</th>
                                                <th className="bg-gray-200 border border-gray-400 px-2 py-1 text-right font-bold">Wartość brutto</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {invoiceItems.map((item, idx) => (
                                                <tr key={item.id} className={idx % 2 === 1 ? 'bg-gray-50' : ''}>
                                                    <td className="border border-gray-300 px-2 py-1 text-center">{item.line_number}</td>
                                                    <td className="border border-gray-300 px-2 py-1 text-blue-600">{item.description}</td>
                                                    <td className="border border-gray-300 px-2 py-1 text-right">{item.quantity} {item.unit}</td>
                                                    <td className="border border-gray-300 px-2 py-1 text-right">{formatNumber(item.unit_price_net)}</td>
                                                    <td className="border border-gray-300 px-2 py-1 text-right">{formatNumber(item.net_amount)}</td>
                                                    <td className="border border-gray-300 px-2 py-1 text-center">{item.vat_rate}</td>
                                                    <td className="border border-gray-300 px-2 py-1 text-right">{formatNumber(item.vat_amount)}</td>
                                                    <td className="border border-gray-300 px-2 py-1 text-right font-bold">{formatNumber(item.gross_amount)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Podsumowanie kwot */}
                                <div className="flex justify-end mb-4">
                                    <div className="text-xs" style={{ minWidth: '280px' }}>
                                        <div className="flex justify-between mb-1">
                                            <span>Wartość netto</span>
                                            <span>{formatNumber(selectedInvoice.net_amount)} PLN</span>
                                        </div>
                                        <div className="flex justify-between mb-1">
                                            <span>Wartość VAT</span>
                                            <span>{formatNumber(selectedInvoice.vat_amount)} PLN</span>
                                        </div>
                                        <div className="flex justify-between font-bold">
                                            <span>Wartość brutto</span>
                                            <span>{formatNumber(selectedInvoice.gross_amount)} PLN</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Do zapłaty */}
                                <div className="mb-4 text-sm">
                                    <div className="flex">
                                        <span className="font-bold whitespace-nowrap">Do zapłaty&nbsp;</span>
                                        <div>
                                            <div>{formatNumber(selectedInvoice.gross_amount)} PLN</div>
                                            <div className="text-xs text-gray-600">słownie: {amountInWords(selectedInvoice.gross_amount)}</div>
                                        </div>
                                    </div>
                                </div>

                                <hr className="border-gray-300 my-3" />

                                {/* Płatność */}
                                <div className="text-sm font-bold mb-2">Płatność</div>
                                <div className="mb-4" style={{ fontSize: '12px' }}>
                                    <div className="mb-1">
                                        <span className="font-bold">Termin płatności:</span> {formatDate(selectedInvoice.payment_due_date)}
                                    </div>
                                    <div className="mb-1">
                                        <span className="font-bold">Płatność:</span> przelew
                                    </div>
                                    {selectedInvoice.seller_bank_account && (
                                        <>
                                            {getBankName(selectedInvoice.seller_bank_account) && (
                                                <div className="mb-1">
                                                    <span className="font-bold">Rachunek bankowy:</span> {getBankName(selectedInvoice.seller_bank_account)}
                                                </div>
                                            )}
                                            <div className="mb-1">
                                                <span className="font-bold">Numer rachunku bankowego:</span> <span className="font-mono">{selectedInvoice.seller_bank_account}</span>
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* KSeF */}
                                {selectedInvoice.ksef_number && (
                                    <>
                                        <hr className="border-gray-300 my-3" />
                                        <div className="text-sm font-bold mb-2">KSeF</div>
                                        <div style={{ fontSize: '12px' }}>
                                            <div className="mb-1">
                                                <span className="font-bold">Numer KSeF:</span> <span className="font-mono">{selectedInvoice.ksef_number}</span>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </main>
            </div>
        );
    }

    // Widok listy faktur
    return (
        <div className="min-h-screen p-4">
            <main className="container mx-auto">
                <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                        <h2 className="text-xl sm:text-2xl font-bold flex items-center">
                            <FaFileInvoice className="mr-2 text-softTeal" />
                            Faktury
                        </h2>

                        <div className="flex gap-2">
                            <button
                                onClick={() => setAsSeller(true)}
                                className={`px-4 py-2 rounded-lg transition ${
                                    asSeller
                                        ? 'bg-softTeal text-white'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                            >
                                Wystawione
                            </button>
                            <button
                                onClick={() => setAsSeller(false)}
                                className={`px-4 py-2 rounded-lg transition ${
                                    !asSeller
                                        ? 'bg-softTeal text-white'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                            >
                                Otrzymane
                            </button>
                        </div>
                    </div>

                    {error && (
                        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                            {error}
                        </div>
                    )}

                    {loading ? (
                        <p className="text-center py-4">Ładowanie faktur...</p>
                    ) : invoices.length === 0 ? (
                        <p className="text-gray-500 text-center py-8">
                            Brak faktur {asSeller ? 'wystawionych' : 'otrzymanych'}
                        </p>
                    ) : (
                        <>
                            {/* Mobile view */}
                            <div className="md:hidden space-y-4">
                                {invoices.map((invoice) => (
                                    <div key={invoice.id} className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
                                        <div className="flex justify-between items-start mb-3">
                                            <div>
                                                <p className="font-bold">{invoice.invoice_number}</p>
                                                <p className="text-sm text-gray-600">{formatDate(invoice.invoice_date)}</p>
                                            </div>
                                            {getStatusBadge(invoice.ksef_status)}
                                        </div>
                                        <div className="mb-2">
                                            <span className="text-gray-600 text-sm">Termin płatności:</span>
                                            <p>{formatDate(invoice.payment_due_date)}</p>
                                        </div>
                                        <div className="mb-2">
                                            <span className="text-gray-600 text-sm">
                                                {asSeller ? 'Nabywca' : 'Sprzedawca'}:
                                            </span>
                                            <p>{userIdToName[asSeller ? invoice.buyer_user_id : invoice.seller_user_id] || '-'}</p>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="font-bold text-lg">{formatCurrency(invoice.gross_amount)}</span>
                                            <div className="flex gap-2">
                                                {asSeller && canSendToKsef(invoice.ksef_status) && (
                                                    <button
                                                        onClick={() => handleSendToKsef(invoice)}
                                                        disabled={sending === invoice.id}
                                                        className="text-softTeal hover:text-warmOrange p-2 disabled:opacity-50"
                                                        title="Wyślij do KSeF"
                                                    >
                                                        <FaPaperPlane size={20} />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => viewInvoiceDetails(invoice)}
                                                    className="text-softTeal hover:text-warmOrange p-2"
                                                    title="Zobacz szczegóły"
                                                >
                                                    <FaEye size={20} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Desktop view */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="min-w-full bg-white border border-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="border px-4 py-2 text-left">Numer faktury</th>
                                            <th className="border px-4 py-2 text-left">Data wystawienia</th>
                                            <th className="border px-4 py-2 text-left">Termin płatności</th>
                                            <th className="border px-4 py-2 text-left">{asSeller ? 'Nabywca' : 'Sprzedawca'}</th>
                                            <th className="border px-4 py-2 text-right">Netto</th>
                                            <th className="border px-4 py-2 text-right">VAT</th>
                                            <th className="border px-4 py-2 text-right">Brutto</th>
                                            <th className="border px-4 py-2 text-center">Status</th>
                                            {asSeller && <th className="border px-4 py-2 text-center">Wyślij do KSeF</th>}
                                            <th className="border px-4 py-2 text-center">Podgląd</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoices.map((invoice) => (
                                            <tr key={invoice.id} className="hover:bg-gray-50">
                                                <td className="border px-4 py-2 font-semibold">{invoice.invoice_number}</td>
                                                <td className="border px-4 py-2">{formatDate(invoice.invoice_date)}</td>
                                                <td className="border px-4 py-2">{formatDate(invoice.payment_due_date)}</td>
                                                <td className="border px-4 py-2">
                                                    {userIdToName[asSeller ? invoice.buyer_user_id : invoice.seller_user_id] || '-'}
                                                </td>
                                                <td className="border px-4 py-2 text-right">{formatCurrency(invoice.net_amount)}</td>
                                                <td className="border px-4 py-2 text-right">{formatCurrency(invoice.vat_amount)}</td>
                                                <td className="border px-4 py-2 text-right font-semibold">{formatCurrency(invoice.gross_amount)}</td>
                                                <td className="border px-4 py-2 text-center">{getStatusBadge(invoice.ksef_status)}</td>
                                                {asSeller && (
                                                    <td className="border px-4 py-2 text-center">
                                                        {canSendToKsef(invoice.ksef_status) ? (
                                                            <button
                                                                onClick={() => handleSendToKsef(invoice)}
                                                                disabled={sending === invoice.id}
                                                                className="text-softTeal hover:text-warmOrange p-1 disabled:opacity-50"
                                                                title="Wyślij do KSeF"
                                                            >
                                                                <FaPaperPlane />
                                                            </button>
                                                        ) : (
                                                            <span className="text-gray-300">-</span>
                                                        )}
                                                    </td>
                                                )}
                                                <td className="border px-4 py-2 text-center">
                                                    <button
                                                        onClick={() => viewInvoiceDetails(invoice)}
                                                        className="text-softTeal hover:text-warmOrange p-1"
                                                        title="Zobacz szczegóły"
                                                    >
                                                        <FaEye />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            </main>
        </div>
    );
};

export default Invoices;
